import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const FISCAL_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const fiscalClient = axios.create({
  baseURL: FISCAL_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

fiscalClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

fiscalClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

export type TransactionStatus =
  | 'pending'
  | 'processing'
  | 'pending_approval'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'refunded';
export type FiscalPaymentMethod = string;
export type MPesaStatus =
  | 'pending'
  | 'initiated'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'cancelled';

export interface OrderItem {
  product_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
}

export interface Transaction {
  id: string;
  reference: string;
  invoice_number: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  payment_method: FiscalPaymentMethod;
  payment_status: TransactionStatus;
  customer_phone: string;
  customer_name: string;
  customer_email: string;
  room_id: string | null;
  booking_id: string | null;
  order_items: OrderItem[];
  notes: string;
  source: string;
  reference_type: string;
  tip_amount: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface PaginatedTransactions {
  data: Transaction[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface RevenueByMethod {
  method: FiscalPaymentMethod;
  total_amount: number;
  transaction_count: number;
}

export interface RevenueBySource {
  source: string;
  total_amount: number;
  tax_amount: number;
  transaction_count: number;
}

export interface TransactionSummary {
  total_count: number;
  total_amount: number;
  total_tax: number;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  by_method: RevenueByMethod[];
  by_source: RevenueBySource[];
}

export interface DailyRevenue {
  date: string;
  total_amount: number;
  tax_amount: number;
  transaction_count: number;
}

export interface VolumePeriod {
  period: string;
  total_amount: number;
  transaction_count: number;
}

export interface TaxSummary {
  date: string;
  source: string;
  tax_amount: number;
  taxable_amount: number;
  transaction_count: number;
}

export interface ETIMSSummary {
  total_count: number;
  accepted_count: number;
  rejected_count: number;
  pending_count: number;
  submitted_count: number;
  failed_count: number;
  total_tax: number;
  accepted_tax: number;
}

export interface MPSettlementSummary {
  total_count: number;
  completed_count: number;
  failed_count: number;
  pending_count: number;
  timeout_count: number;
  initiated_count: number;
  waiting_count: number;
  total_amount: number;
  completed_amount: number;
  failed_amount: number;
}

export interface MPesaTransaction {
  id: string;
  transaction_id: string;
  checkout_request_id: string;
  mpesa_receipt_number: string;
  conversation_id: string;
  originator_conversation_id: string;
  transaction_type: string;
  amount: number;
  party_a: string;
  party_b: string;
  status: MPesaStatus;
  status_reason: string;
  initiated_at: string | null;
  processed_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface RevenuePerformance {
  total_revenue: number;
  target_revenue: number;
  variance: number;
  variance_percent: number;
  period_comparison: DailyRevenue[];
}

export interface BestSeller {
  item_name: string;
  item_id: string;
  quantity_sold: number;
  total_revenue: number;
  transaction_count: number;
}

export interface ProcessTransactionRequest {
  order_items: OrderItem[];
  subtotal: number;
  tax_total: number;
  discount_total?: number;
  grand_total: number;
  payment_method: FiscalPaymentMethod;
  customer_phone: string;
  customer_name?: string;
  customer_email?: string;
  room_id?: string;
  booking_id?: string;
  notes?: string;
  source?: string;
}

export interface TransactionFilters {
  page?: number;
  page_size?: number;
  status?: TransactionStatus;
  payment_method?: FiscalPaymentMethod;
  source?: string;
  date_from?: string;
  date_to?: string;
  min_amount?: number;
  max_amount?: number;
  q?: string;
}

export interface PaymentIntentRequest {
  amount: number;
  currency?: string;
  paymentMethod: string;
  phoneNumber?: string;
  referenceId: string;
  referenceType?: string;
  tipAmount?: number;
  taxAmount?: number;
  metadata?: Record<string, unknown>;
}

export interface PaymentIntentResponse {
  transactionId: string;
  status: string;
  message: string;
}

export const fiscalService = {
  listTransactions: async (filters?: TransactionFilters): Promise<PaginatedTransactions> => {
    const res = await fiscalClient.get<PaginatedTransactions>('/api/v1/transactions', {
      params: filters,
    });
    return res.data;
  },

  getTransaction: (id: string) => fiscalClient.get<Transaction>(`/api/v1/transactions/${id}`),

  getTransactionByRef: (ref: string) =>
    fiscalClient.get<Transaction>(`/api/v1/transactions/reference/${ref}`),

  processTransaction: (data: ProcessTransactionRequest) =>
    fiscalClient.post('/api/v1/transactions/process', data),

  checkPaymentStatus: (id: string) => fiscalClient.get(`/api/v1/transactions/${id}/status`),

  getSummary: async (): Promise<TransactionSummary> => {
    const res = await fiscalClient.get<TransactionSummary>('/api/v1/transactions/summary');
    return res.data;
  },

  getDailyRevenue: async (dateFrom?: string, dateTo?: string): Promise<DailyRevenue[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<{ data: DailyRevenue[] }>(
      '/api/v1/fiscal-reporting/revenue/daily',
      { params },
    );
    return res.data.data;
  },

  getRevenueBySource: async (dateFrom?: string, dateTo?: string): Promise<RevenueBySource[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<{ data: RevenueBySource[] }>(
      '/api/v1/fiscal-reporting/revenue/by-source',
      { params },
    );
    return res.data.data;
  },

  getRevenueByMethod: async (dateFrom?: string, dateTo?: string): Promise<RevenueByMethod[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<{ data: RevenueByMethod[] }>(
      '/api/v1/fiscal-reporting/revenue/by-method',
      { params },
    );
    return res.data.data;
  },

  getTaxSummary: async (dateFrom?: string, dateTo?: string): Promise<TaxSummary[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<{ data: TaxSummary[] }>(
      '/api/v1/fiscal-reporting/tax/summary',
      { params },
    );
    return res.data.data;
  },

  getTransactionVolume: async (
    dateFrom?: string,
    dateTo?: string,
    groupBy?: string,
  ): Promise<VolumePeriod[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (groupBy) params.group_by = groupBy;
    const res = await fiscalClient.get<{ data: VolumePeriod[] }>(
      '/api/v1/fiscal-reporting/volume',
      { params },
    );
    return res.data.data;
  },

  getETIMSSummary: async (): Promise<ETIMSSummary> => {
    const res = await fiscalClient.get<ETIMSSummary>('/api/v1/fiscal-reporting/etims/summary');
    return res.data;
  },

  getMpesaSettlement: async (dateFrom?: string, dateTo?: string): Promise<MPSettlementSummary> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<MPSettlementSummary>(
      '/api/v1/fiscal-reporting/mpesa/settlement',
      { params },
    );
    return res.data;
  },

  listMpesaTransactions: async (
    dateFrom?: string,
    dateTo?: string,
  ): Promise<MPesaTransaction[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<{ data: MPesaTransaction[] }>(
      '/api/v1/fiscal-reporting/mpesa/transactions',
      { params },
    );
    return res.data.data;
  },

  getRevenuePerformance: async (
    dateFrom?: string,
    dateTo?: string,
  ): Promise<RevenuePerformance> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await fiscalClient.get<RevenuePerformance>(
      '/api/v1/fiscal-reporting/revenue/performance',
      { params },
    );
    return res.data;
  },

  getBestSellers: async (
    dateFrom?: string,
    dateTo?: string,
    limit?: number,
  ): Promise<BestSeller[]> => {
    const params: Record<string, string | number> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (limit) params.limit = limit;
    const res = await fiscalClient.get<{ data: BestSeller[] }>(
      '/api/v1/fiscal-reporting/food/best-sellers',
      { params },
    );
    return res.data.data;
  },

  submitPaymentIntent: (data: PaymentIntentRequest) =>
    fiscalClient.post<PaymentIntentResponse>('/api/v1/payment-intents', data),

  getPaymentIntent: (id: string) =>
    fiscalClient.get<PaymentIntentResponse>(`/api/v1/payment-intents/${id}`),

  approvePaymentIntent: (id: string) =>
    fiscalClient.post<PaymentIntentResponse>(`/api/v1/payment-intents/${id}/approve`),
};
